import { MergeView } from '@codemirror/merge';
import { EditorView } from '@codemirror/view';
import { EditorState, type Extension } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { json } from '@codemirror/lang-json';
import {
  normalizeForSemanticDiff,
  structuralDiff,
  DiffLimitError,
  type StructuralDiffResult,
  type DiffEntry,
} from '../core/diff';
import { JsonSyntaxError } from '../core/json';
import { changedLineGutter, refreshChangedGutters } from '../editor/changedGutter';
import { el, button } from './dom';

type DiffMode = 'raw' | 'semantic';

/** Cap how many structural entries we list in the DOM (data is already capped). */
const MAX_LISTED_ENTRIES = 200;

const LEFT_SAMPLE = `{
  "name": "widget",
  "version": 1,
  "tags": ["a", "b"],
  "active": true
}`;

const RIGHT_SAMPLE = `{
  "active": false,
  "name": "widget",
  "tags": ["a", "b", "c"],
  "version": 2
}`;

/** Diff tab: side-by-side comparison with raw and semantic modes. */
export function createDiffView(): HTMLElement {
  let mode: DiffMode = 'raw';
  let leftRaw = LEFT_SAMPLE;
  let rightRaw = RIGHT_SAMPLE;
  let summaryDebounce: number | undefined;

  const host = el('div', { class: 'merge-host' });
  const banner = el('div', { class: 'banner', hidden: true, role: 'status' });
  const summary = el('div', {
    class: 'diff-summary',
    role: 'region',
    'aria-label': 'Structural differences',
  });
  let merge: MergeView | null = null;

  function sideExtensions(readOnly: boolean): Extension[] {
    const ext: Extension[] = [
      basicSetup,
      json(),
      EditorView.lineWrapping,
      changedLineGutter(),
    ];
    if (readOnly) {
      ext.push(EditorState.readOnly.of(true), EditorView.editable.of(false));
    } else {
      ext.push(
        EditorView.updateListener.of((u) => {
          if (u.docChanged) scheduleSummary();
        }),
      );
    }
    return ext;
  }

  function currentTexts(): { left: string; right: string } {
    if (merge && mode === 'raw') {
      return {
        left: merge.a.state.doc.toString(),
        right: merge.b.state.doc.toString(),
      };
    }
    return { left: leftRaw, right: rightRaw };
  }

  function captureRawFromEditors(): void {
    if (merge && mode === 'raw') {
      leftRaw = merge.a.state.doc.toString();
      rightRaw = merge.b.state.doc.toString();
    }
  }

  function setBanner(message: string | null): void {
    if (message) {
      banner.textContent = message;
      banner.hidden = false;
    } else {
      banner.replaceChildren();
      banner.hidden = true;
    }
  }

  function scheduleSummary(): void {
    if (summaryDebounce !== undefined) window.clearTimeout(summaryDebounce);
    summaryDebounce = window.setTimeout(renderSummary, 300);
  }

  function renderSummary(): void {
    const { left, right } = currentTexts();
    summary.replaceChildren();

    if (left.trim() === '' || right.trim() === '') {
      summary.dataset.state = 'idle';
      summary.append(
        el('span', { class: 'diff-summary-note' }, [
          'Enter JSON on both sides to see a structural summary.',
        ]),
      );
      return;
    }

    let result: StructuralDiffResult;
    try {
      result = structuralDiff(left, right);
    } catch (e) {
      summary.dataset.state = 'error';
      if (e instanceof JsonSyntaxError) {
        summary.append(
          el('span', { class: 'diff-summary-note error' }, [
            `Structural summary unavailable - invalid JSON (line ${e.pos.line}, col ${e.pos.column}).`,
          ]),
        );
      } else if (e instanceof DiffLimitError) {
        summary.append(el('span', { class: 'diff-summary-note error' }, [e.message]));
      } else {
        summary.append(
          el('span', { class: 'diff-summary-note error' }, [
            'Structural summary unavailable.',
          ]),
        );
      }
      return;
    }

    const totalDiffs = result.added + result.removed + result.changed;
    summary.dataset.state = totalDiffs === 0 ? 'ok' : 'diff';

    summary.append(
      el('div', { class: 'diff-counts' }, [
        el('span', { class: 'diff-count added' }, [`+${result.added} added`]),
        el('span', { class: 'diff-count removed' }, [`-${result.removed} removed`]),
        el('span', { class: 'diff-count changed' }, [`~${result.changed} changed`]),
      ]),
    );

    const notes: string[] = ['Arrays are compared by index.'];
    if (result.duplicateKeys) notes.push('Duplicate keys were collapsed (last value wins).');
    if (result.truncated) {
      notes.push(
        `Reached the ${result.entries.length}-difference limit; counts and the list below reflect only the differences shown (more were omitted).`,
      );
    }
    summary.append(el('div', { class: 'diff-summary-note' }, [notes.join(' ')]));

    if (totalDiffs === 0) {
      summary.append(el('div', { class: 'diff-summary-note ok' }, ['Structurally identical.']));
      return;
    }

    const list = el('ul', { class: 'diff-entries' });
    const shown = result.entries.slice(0, MAX_LISTED_ENTRIES);
    for (const entry of shown) list.append(renderEntry(entry));
    summary.append(list);
    if (result.entries.length > shown.length) {
      summary.append(
        el('div', { class: 'diff-summary-note' }, [
          `+${result.entries.length - shown.length} more differences not listed.`,
        ]),
      );
    }
  }

  function renderEntry(entry: DiffEntry): HTMLElement {
    const sign = entry.kind === 'added' ? '+' : entry.kind === 'removed' ? '-' : '~';
    const children: Array<Node | string> = [
      el('span', { class: `diff-entry-kind ${entry.kind}` }, [sign]),
      el('span', { class: 'diff-entry-path' }, [entry.path]),
    ];
    if (entry.kind === 'changed') {
      children.push(
        el('span', { class: 'diff-entry-values' }, [
          el('span', { class: 'diff-old' }, [entry.left ?? '']),
          el('span', { class: 'diff-arrow' }, [' → ']),
          el('span', { class: 'diff-new' }, [entry.right ?? '']),
        ]),
      );
    } else {
      children.push(
        el('span', { class: 'diff-entry-values' }, [
          el('span', { class: entry.kind === 'added' ? 'diff-new' : 'diff-old' }, [
            entry.kind === 'added' ? entry.right ?? '' : entry.left ?? '',
          ]),
        ]),
      );
    }
    return el('li', { class: `diff-entry ${entry.kind}` }, children);
  }

  function build(): void {
    merge?.destroy();
    host.replaceChildren();

    let leftDoc = leftRaw;
    let rightDoc = rightRaw;
    let readOnly = false;

    if (mode === 'semantic') {
      readOnly = true;
      const problems: string[] = [];
      try {
        leftDoc = normalizeForSemanticDiff(leftRaw);
      } catch (e) {
        problems.push(`Left is invalid JSON${sideDetail(e)}`);
      }
      try {
        rightDoc = normalizeForSemanticDiff(rightRaw);
      } catch (e) {
        problems.push(`Right is invalid JSON${sideDetail(e)}`);
      }
      setBanner(
        problems.length > 0
          ? `${problems.join(' · ')}. Showing raw text for that side.`
          : 'Semantic mode: keys sorted and whitespace normalized. Duplicate keys and number precision may be normalized away.',
      );
    } else {
      setBanner(null);
    }

    merge = new MergeView({
      parent: host,
      a: { doc: leftDoc, extensions: sideExtensions(readOnly) },
      b: { doc: rightDoc, extensions: sideExtensions(readOnly) },
      gutter: true,
      collapseUnchanged: { margin: 3, minSize: 6 },
    });

    // Chunk state may not be ready during the editors' initial field setup;
    // force both gutters to recompute now that the merge view is fully built.
    refreshChangedGutters(merge.a, merge.b);
    renderSummary();
  }

  function sideDetail(e: unknown): string {
    if (e instanceof JsonSyntaxError) {
      return ` (line ${e.pos.line}, col ${e.pos.column})`;
    }
    return '';
  }

  const rawBtn = button('Raw diff', { class: 'btn toggle' });
  const semanticBtn = button('Semantic diff', { class: 'btn toggle' });

  function setMode(next: DiffMode): void {
    if (next === mode) return;
    captureRawFromEditors();
    mode = next;
    rawBtn.classList.toggle('active', mode === 'raw');
    semanticBtn.classList.toggle('active', mode === 'semantic');
    build();
  }

  rawBtn.addEventListener('click', () => setMode('raw'));
  semanticBtn.addEventListener('click', () => setMode('semantic'));
  rawBtn.classList.add('active');

  const swapBtn = button('Swap sides');
  swapBtn.addEventListener('click', () => {
    captureRawFromEditors();
    [leftRaw, rightRaw] = [rightRaw, leftRaw];
    build();
  });

  const clearBtn = button('Clear');
  clearBtn.addEventListener('click', () => {
    leftRaw = '';
    rightRaw = '';
    if (mode !== 'raw') {
      mode = 'raw';
      rawBtn.classList.add('active');
      semanticBtn.classList.remove('active');
    }
    build();
  });

  const toolbar = el('div', { class: 'toolbar' }, [
    rawBtn,
    semanticBtn,
    el('span', { class: 'spacer' }),
    swapBtn,
    clearBtn,
  ]);

  build();
  return el('section', { class: 'panel' }, [toolbar, banner, summary, host]);
}
